// ─── Types ────────────────────────────────────────────────────────────────────

export type Artwork = {
  id: string
  title: string
  description: string | null
  priceNok: number
  widthCm: number | null
  heightCm: number | null
  medium: string
  status: 'available' | 'sold'
  createdAt: string
  artistId?: string
  artistName: string
  artistSlug: string
  imageUrl: string
  canPickup: boolean
  pickupLocation: string | null
  canShip: boolean
  shippingNok: number
  shippingOption: string | null
}

export type ArtistProfile = {
  id: string
  name: string
  bio: string | null
  profileSlug: string
  createdAt: string
  artworks: Artwork[]
}

// The authenticated artist — subset returned by login/register/me
export type Artist = {
  id: string
  email: string
  name: string
  profileSlug: string
  stripeConnected: boolean
  phone: string | null
}

// ─── Public gallery ───────────────────────────────────────────────────────────

export async function fetchMediums(): Promise<string[]> {
  const res = await fetch('/api/artworks/mediums')
  if (!res.ok) throw new Error('Failed to load mediums')
  return res.json()
}

export async function fetchArtworkArtists(): Promise<{ name: string; slug: string }[]> {
  const res = await fetch('/api/artworks/artists')
  if (!res.ok) throw new Error('Failed to load artists')
  return res.json()
}

export async function fetchArtworks(params?: {
  medium?: string
  artistSlug?: string
  maxPrice?: string
}): Promise<Artwork[]> {
  const url = new URL('/api/artworks', window.location.origin)
  if (params?.medium) url.searchParams.set('medium', params.medium)
  if (params?.artistSlug) url.searchParams.set('artistSlug', params.artistSlug)
  if (params?.maxPrice) url.searchParams.set('maxPrice', params.maxPrice)
  const res = await fetch(url.toString())
  if (!res.ok) throw new Error('Failed to load artworks')
  return res.json()
}

export async function fetchArtwork(id: string): Promise<Artwork | null> {
  const res = await fetch(`/api/artworks/${id}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to load artwork')
  return res.json()
}

export type ArtworkOrder = {
  id: string
  buyerName: string
  buyerEmail: string
  buyerPhone: string | null
  buyerAddress: string | null
  shippingMethod: string | null
  shippingNok: number
  createdAt: string
}

export async function fetchArtworkOrder(artworkId: string): Promise<ArtworkOrder | null> {
  const res = await fetch(`/api/artworks/${artworkId}/order`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to load order')
  return res.json()
}

export async function fetchArtist(slug: string): Promise<ArtistProfile | null> {
  const res = await fetch(`/api/artists/${slug}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to load artist')
  return res.json()
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

async function throwOnError(res: Response): Promise<Response> {
  if (!res.ok) {
    const body = await res.json().catch(() => null) as { error?: string } | null
    throw new Error(body?.error ?? `Request failed (${res.status})`)
  }
  return res
}

export async function fetchMe(): Promise<Artist | null> {
  const res = await fetch('/api/auth/me')
  if (res.status === 401) return null
  if (!res.ok) throw new Error('Failed to load session')
  return res.json()
}

export async function login(email: string, password: string): Promise<Artist> {
  const res = await throwOnError(
    await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
  )
  return res.json()
}

export async function register(
  email: string,
  password: string,
  name: string,
  phone?: string
): Promise<Artist> {
  const res = await throwOnError(
    await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name, phone: phone || undefined }),
    })
  )
  return res.json()
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST' })
}

export async function deleteAccount(): Promise<void> {
  await throwOnError(await fetch('/api/auth/account', { method: 'DELETE' }))
}

// ─── Orders ───────────────────────────────────────────────────────────────────

export type Order = {
  id: string
  status: string
  artworkId: string
  artworkTitle: string
  artworkImageUrl: string
  priceNok: number
  buyerName: string
  shippingMethod: string | null
  shippingNok: number
  buyerAddress: string | null
  buyerPhone: string | null
  artistPhone: string | null
  createdAt: string
}

export async function fetchOrder(id: string): Promise<Order | null> {
  const res = await fetch(`/api/orders/${id}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to load order')
  return res.json()
}

export type ArtistOrder = {
  id: string
  artworkTitle: string
  priceNok: number
  shippingNok: number
  shippingMethod: string | null
  buyerAddress: string | null
  buyerName: string
  buyerPhone: string | null
  createdAt: string
}

export async function fetchArtistOrders(): Promise<ArtistOrder[]> {
  const res = await throwOnError(await fetch('/api/auth/orders'))
  return res.json()
}

export async function updateProfile(phone: string | null): Promise<void> {
  await throwOnError(
    await fetch('/api/auth/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone }),
    })
  )
}

export async function initiateCheckout(
  artworkId: string,
  buyer: { buyerEmail: string; buyerName: string; buyerPhone?: string; shippingMethod: 'pickup' | 'ship' }
): Promise<{ url: string; orderId: string }> {
  const res = await throwOnError(
    await fetch(`/api/checkout/${artworkId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buyer),
    })
  )
  return res.json()
}

// ─── Stripe Connect ───────────────────────────────────────────────────────────

export async function startStripeOnboarding(): Promise<{ url: string }> {
  const res = await throwOnError(
    await fetch('/api/connect/onboard', { method: 'POST' })
  )
  return res.json()
}

export async function openStripeDashboard(): Promise<{ url: string }> {
  const res = await throwOnError(
    await fetch('/api/connect/dashboard', { method: 'POST' })
  )
  return res.json()
}

// ─── Artist dashboard ─────────────────────────────────────────────────────────

export async function updateArtwork(id: string, fields: {
  title?: string; description?: string; medium?: string
  priceNok?: number; widthCm?: number | null; heightCm?: number | null
  canPickup?: boolean; pickupLocation?: string | null; shippingOption?: string | null
}): Promise<Artwork> {
  const res = await throwOnError(
    await fetch(`/api/artworks/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: fields.title,
        description: fields.description,
        medium: fields.medium,
        price_nok: fields.priceNok,
        width_cm: fields.widthCm,
        height_cm: fields.heightCm,
        can_pickup: fields.canPickup,
        pickup_location: fields.pickupLocation,
        shipping_option: fields.shippingOption,
      }),
    })
  )
  return res.json()
}

export async function uploadArtwork(formData: FormData): Promise<Artwork> {
  const res = await throwOnError(
    await fetch('/api/artworks', { method: 'POST', body: formData })
  )
  return res.json()
}

export async function deleteArtwork(id: string): Promise<void> {
  await throwOnError(await fetch(`/api/artworks/${id}`, { method: 'DELETE' }))
}
